import { useCallback, useState } from 'react';
import { Snackbar, Alert } from '@mui/material';

export type SonosFeedbackSeverity = 'success' | 'error' | 'info';

export interface SonosFeedbackState {
  open: boolean;
  message: string;
  severity: SonosFeedbackSeverity;
}

export function useSonosFeedback() {
  const [snackbar, setSnackbar] = useState<SonosFeedbackState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const notify = useCallback((message: string, severity: SonosFeedbackSeverity = 'info') => {
    // Blindaje: nunca guardar algo que no sea string (un objeto haría crashear el
    // render del snackbar -> pantalla negra). Coacciona de forma segura.
    let text: string;
    if (typeof message === 'string') {
      text = message;
    } else if (message && typeof message === 'object') {
      const m = message as { detail?: unknown; message?: unknown };
      text = typeof m.detail === 'string'
        ? m.detail
        : typeof m.message === 'string'
          ? m.message
          : 'Error';
    } else {
      text = message == null ? '' : String(message);
    }
    setSnackbar({ open: true, message: text, severity });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return { snackbar, notify, closeSnackbar };
}

interface SonosFeedbackSnackbarProps {
  state: SonosFeedbackState;
  onClose: () => void;
}

export function SonosFeedbackSnackbar({ state, onClose }: SonosFeedbackSnackbarProps) {
  return (
    <Snackbar
      open={state.open}
      autoHideDuration={4000}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ top: '16px !important', maxWidth: 'calc(100vw - 32px)', zIndex: 1900 }}
    >
      <Alert onClose={onClose} severity={state.severity} sx={{ width: '100%' }}>
        {state.message}
      </Alert>
    </Snackbar>
  );
}

export type SonosMessageHandler = (message: string, severity?: SonosFeedbackSeverity) => void;
